import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  'widget.title': {
    defaultMessage: 'Interactive walkthrough',
    id: 'WrS1fW',
  },
  'widget.subtitle': {
    defaultMessage: 'See IBM Cloud Pak for AIOps in action.',
    id: 'tufHPo',
  },
  'template.nextSteps': { defaultMessage: 'Next Steps', id: 'xe05Cm' },
  'template.menu': { defaultMessage: 'Menu', id: 'tKMlOc' },
  'template.complete': { defaultMessage: 'Complete', id: 'U78NhE' },
  'scenarioA.title': {
    defaultMessage: 'Noise reduction and root cause analysis',
    id: 'UDQM74',
  },
  'scenarioA.stepA1.title': {
    defaultMessage: 'Review an incident',
    id: 'jY5ZKr',
  },
  'scenarioA.stepA1.body': {
    defaultMessage: `<p>An incident is a collection of insights derived from different data sources that are presented in one place to help support rapid resolution.</p><p>Looking at this incident, you notice right away that the priority is 1. This means it's business-critical and needs to be resolved as soon as possible.</p>`,
    id: '2dmkHN',
  },
  'scenarioA.stepA1.alt': {
    defaultMessage:
      'Slack notifications display insights from various data sources in one place and emphasize incident urgency.',
    id: '/eMyWY',
  },
  'scenarioA.stepA2.title': {
    defaultMessage: 'Review probable cause in the incident overview',
    id: 'LNpTv8',
  },
  'scenarioA.stepA2.body': {
    defaultMessage:
      '<p>Reviewing the incident overview in the UI console gives you more context to understand what has happened and how to fix it.</p>',
    id: '+HcS2C',
  },
  'scenarioA.stepA2.alt': {
    defaultMessage:
      'Review incident overview in UI console for context, probable cause, and fix instructions.',
    id: '5aurwR',
  },
  'scenarioA.stepA3.title': {
    defaultMessage: 'Review associated alerts',
    id: 'zL8NCS',
  },
  'scenarioA.stepA3.body': {
    defaultMessage:
      '<p>Select the Alerts tab to review alerts that are associated with the incident.</p>',
    id: 'iGVLmv',
  },
  'scenarioA.stepA3.alt': {
    defaultMessage:
      'View resource relationships connected to incident in Topology tab.',
    id: 'EGZjmm',
  },
  'scenarioA.stepA4.title': {
    defaultMessage: 'View topology details',
    id: 'Xx6GL7',
  },
  'scenarioA.stepA4.body': {
    defaultMessage:
      '<p>Select the Topology tab to see relationships between resources that are connected to the incident.</p>',
    id: 'Z0Mc7T',
  },
  'scenarioA.stepA4.alt': {
    defaultMessage: 'Review alerts associated with incident in Alerts tab.',
    id: 'kKEYOt',
  },
  // scenario B
  'scenarioB.stepB1.title': {
    defaultMessage: 'Early detection',
    id: '9gWUII',
  },
  'scenarioB.stepB1.body': {
    defaultMessage:
      '<p>Learn how Cloud Pak for AIOps uses AI to provide you with early warning of potential outages.</p>',
    id: '0Bhdx9',
  },
  'scenarioB.stepB1.alt': {
    defaultMessage:
      'Cloud Pak for AIOps provides early warning of potential outages using AI in Stories & Alerts hub.',
    id: 'cwukZs',
  },
  'scenarioB.stepB2.title': {
    defaultMessage: 'Preview metric anomaly information for an alert',
    id: 'algcWR',
  },
  'scenarioB.stepB2.body': {
    defaultMessage:
      '<p>Selecting an alert opens a sidebar with additional information. The Metric anomaly details section displays how the latency metric has varied in the past three days and includes projections for the future.</p><p>You can see that the latency metric has started increasing and has gone out of baseline, marked with a red bar. You can also see projections for the future in purple, indicating that the latency is likely to get worse.</p>',
    id: 'YcUPKC',
  },
  'scenarioB.stepB2.alt': {
    defaultMessage:
      'Open metric alert sidebar for additional info on metric anomalies and variations.',
    id: 'LOVpXN',
  },
  'scenarioB.stepB3.title': {
    defaultMessage: 'View the anomaly details',
    id: 'jEQYJo',
  },
  'scenarioB.stepB3.body': {
    defaultMessage:
      '<p>Clicking the link to view the expanded chart enables you to see the graph in greater detail. You can hover over the graph to see individual data points.</p><p>The red bar highlights where the database service response time has gone out of baseline. </p>',
    id: 'tj8omd',
  },
  'scenarioB.stepB3.alt': {
    defaultMessage:
      'View expanded chart for metric anomaly details and hover over graph to see data points.',
    id: '5bscAT',
  },
  'scenarioB.stepB4.title': {
    defaultMessage: 'View the anomaly details',
    id: 'jEQYJo',
  },
  'scenarioB.stepB4.body': {
    defaultMessage:
      '<p>You can see related alerts in the table following the graph. The first related alert indicates that the number of threads available for connections has decreased. This alert is also a metric anomaly, so you select the alert to superimpose it on the graph. You can see from the graph that the database latency started going out of bounds first, and that a few seconds after this, the number of threads started decreasing and going out of bounds.</p>',
    id: 'fJq66r',
  },
  'scenarioB.stepB4.alt': {
    defaultMessage:
      'View related alerts in table next to graph to compare metrics.',
    id: 'n89srT',
  },
  'scenarioB.stepB5.title': {
    defaultMessage: 'Add other related alerts',
    id: 'aR6cvZ',
  },
  'scenarioB.stepB5.body': {
    defaultMessage:
      '<p>The other related alerts tell you that the catalog microservice is showing slow response time and users are beginning to get errors when they log in, as evidenced by the load balancer error. Selecting the catalog service alert displays a blue bar on the graph indicating when that alert started occurring.</p><p>You can clearly see that the small number of connection threads is severely impacting performance. Your conclusion: the database service must be restarted.</p>',
    id: 'Yp3DaX',
  },
  'scenarioB.stepB5.alt': {
    defaultMessage:
      'Add related alerts to overlay events and metrics in graph.',
    id: 'aPnGZj',
  },
  'scenarioB.stepB6.title': {
    defaultMessage: 'Review the topology',
    id: 'tkOqTS',
  },
  'scenarioB.stepB6.body': {
    defaultMessage:
      '<p>You close the expanded graph view and review the topology associated with this latency alert in the Topology section of the sidebar. This shows the context of the Kubernetes environment immediately associated with the database service on which this alert occurred. You can see that there is an alert on the catalog microservice, which needs data from the database.</p>',
    id: 'G8WwpE',
  },
  'scenarioB.stepB6.alt': {
    defaultMessage:
      'Review topology associated with latency alert in Topology section of sidebar.',
    id: '5ziee0',
  },
  'scenarioB.stepB7.title': {
    defaultMessage: 'Create a policy',
    id: '+r54M+',
  },
  'scenarioB.stepB7.body': {
    defaultMessage:
      '<p>After restarting the database service, you decide to create an incident policy that will pull all of these alerts together. Your policy will automatically generate an incident when latency alerts from the database service start to violate the baseline.</p>',
    id: 'V4G6A+',
  },
  'scenarioB.stepB7.alt': {
    defaultMessage:
      'View automation policies that group alerts together in Automation section of sidebar.',
    id: 'iW58DV',
  },
  'scenarioB.title': {
    defaultMessage: 'Early detection',
    id: '9gWUII',
  },
  // scenario C
  'scenarioC.stepC1.title': {
    defaultMessage: 'Automated remediation with runbooks ',
    id: '8cZ4mk',
  },
  'scenarioC.stepC1.body': {
    defaultMessage:
      '<p>Learn how Cloud Pak for AIOps uses runbooks to help increase the efficiency of IT operations by automating procedures.</p>',
    id: 'pECXb5',
  },
  'scenarioC.stepC1.alt': {
    defaultMessage:
      ' Explore the "Automations" section to learn how Cloud Pak for AIOps uses runbooks to automate procedures.',
    id: 'jmWX0t',
  },
  'scenarioC.stepC2.title': {
    defaultMessage: 'What are runbooks?',
    id: '6bWWpy',
  },
  'scenarioC.stepC2.body': {
    defaultMessage:
      '<p>A runbook is a controlled set of automated and manual steps that support system and network operational processes. They can orchestrate all types of infrastructure elements, such as applications, network components, or servers.</p>',
    id: 'KJfKaQ',
  },
  'scenarioC.stepC2.alt': {
    defaultMessage: 'Orchestrate infrastructure elements with runbooks.',
    id: 'Etwg0L',
  },
  'scenarioC.stepC3.title': {
    defaultMessage: 'Create a runbook',
    id: 'RkD4/C',
  },
  'scenarioC.stepC3.body': {
    defaultMessage:
      '<p>If you have an automated procedure or if you need to guide operators through a manual process for resolving problems, you can create your own runbook by defining the required steps to resolve a situation. Runbooks that only contain fully automated procedures do not require any interaction from an operator.</p>',
    id: '2lblDz',
  },
  'scenarioC.stepC3.alt': {
    defaultMessage:
      'Create a runbook by defining required steps to resolve a situation.',
    id: 'ajyNv4',
  },
  'scenarioC.stepC4.title': {
    defaultMessage: 'View the new runbook',
    id: 'gBBYmB',
  },
  'scenarioC.stepC4.body': {
    defaultMessage:
      '<p>After you create a runbook, it will be included in the Library.</p>',
    id: 'k36SXL',
  },
  'scenarioC.stepC4.alt': {
    defaultMessage: 'View created runbooks in Library tab.',
    id: 'vWnOYE',
  },
  'scenarioC.stepC5.title': {
    defaultMessage: 'Incident remediation with runbooks',
    id: 'gWrCHY',
  },
  'scenarioC.stepC5.body': {
    defaultMessage:
      '<p>Once you’ve created runbooks, they can be associated to alerts and used to solve issues.</p>',
    id: '+OUsMa',
  },
  'scenarioC.stepC5.alt': {
    defaultMessage:
      'Associate runbooks with alerts in Stories & Alerts hub to solve issues.',
    id: 'nWRAKv',
  },
  'scenarioC.stepC6.title': {
    defaultMessage: 'Drill into a incident',
    id: '7/YArN',
  },
  'scenarioC.stepC6.body': {
    defaultMessage:
      '<p>Select an incident to see if there are recommended runbooks.</p>',
    id: 'P/MQui',
  },
  'scenarioC.stepC6.alt': {
    defaultMessage:
      'Check for recommended runbooks for an incident in Stories view.',
    id: 'FBljJ4',
  },
  'scenarioC.stepC7.title': {
    defaultMessage: 'Review available runbooks',
    id: 'gT10Mm',
  },
  'scenarioC.stepC7.body': {
    defaultMessage:
      '<p>Select an alert from the list of probable cause alerts and review the runbook that is recommended for solving the issue.</p>',
    id: 'Ay74bb',
  },
  'scenarioC.stepC7.alt': {
    defaultMessage: 'Review available runbooks in Overview tab of Stories hub.',
    id: 'Ys0w7E',
  },
  'scenarioC.stepC8.title': {
    defaultMessage: 'Run a runbook',
    id: '4fPwUU',
  },
  'scenarioC.stepC8.body': {
    defaultMessage:
      '<p>Click Run and review the runbook steps. You can get more information and also review additional details.</p>',
    id: '7bMOZ3',
  },
  'scenarioC.stepC8.alt': {
    defaultMessage: 'Click "Run" to review Runbook steps.',
    id: 'wgrGFP',
  },
  'scenarioC.stepC9.title': {
    defaultMessage: 'Run a runbook',
    id: '4fPwUU',
  },
  'scenarioC.stepC9.body': {
    defaultMessage:
      '<p>Click Start runbook and step through the procedure.</p>',
    id: 'N34UqN',
  },
  'scenarioC.stepC9.alt': {
    defaultMessage:
      'Step through Runbook procedure by clicking "Start Runbook."',
    id: '4Ay4p4',
  },
  'scenarioC.stepC10.title': {
    defaultMessage: 'Leave feedback',
    id: 'gBYxQF',
  },
  'scenarioC.stepC10.body': {
    defaultMessage:
      '<p>Once the runbook completes successfully, rate it and leave comments to help users in the future.</p>',
    id: '8yMI6k',
  },
  'scenarioC.stepC10.alt': {
    defaultMessage: 'Rate and leave comments on completed Runbooks.',
    id: 'NhlhQd',
  },
  'scenarioC.title': {
    defaultMessage: 'Automated remediation',
    id: 'ADl8Lo',
  },
  //scenario D
  'scenarioD.stepD1.title': {
    defaultMessage: 'See results',
    id: 'CpNb11',
  },
  'scenarioD.stepD1.body': {
    defaultMessage:
      '<p>View key insights and learn how IBM Cloud Pak for AIOps can help your organization to anticipate, troubleshoot, and resolve IT incidents.</p>',
    id: 'QM3yYD',
  },
  'scenarioD.stepD1.alt': {
    defaultMessage:
      'View AIOps Insights dashboard to monitor KPIs like MTTR, noise reduction, incident closure rate, and runbook automation.',
    id: 'R+tSPg',
  },
  'scenarioD.stepD2.title': {
    defaultMessage: 'Understand the impact',
    id: 'kaOlHe',
  },
  'scenarioD.stepD2.body': {
    defaultMessage:
      '<p>The AIOps insights dashboard includes a set of predefined interactive charts that show you, at a glance, what is going on in your environment. It provides a high-level view of key indicators from different tools and analysis to help you understand the overall efficiency of your ITOps. You can use these indicators and included metrics to track how Cloud Pak for AIOps is improving operations and efficiency for your team.</p>',
    id: 'GuN5NU',
  },
  'scenarioD.stepD2.alt': {
    defaultMessage:
      'Explore high-level view of key indicators from tools in AIOps Insights dashboard to understand impact.',
    id: 'aq0fa6',
  },
  'scenarioD.title': {
    defaultMessage: 'See results',
    id: 'CpNb11',
  },
  'template.end.cta': {
    defaultMessage: 'Start your free trial',
    id: 'FQm0Au',
  },
  'template.end.title': {
    defaultMessage: 'Scenario complete!',
    id: 'OYgsMU',
  },
});

export type messageType = keyof typeof messages;
